// routeLink — a Google Maps driving route through a set of properties, for a
// site-visit day (#3076). No API key: the public "Maps URLs" format
// (https://developers.google.com/maps/documentation/urls/get-started).
//
// Google allows a destination plus at most 9 waypoints per link, and starts
// from the user's current location when no origin is given. A longer list is
// split into consecutive legs of up to 10 stops, each its own link.

export interface RouteStop {
  lat: number | null | undefined;
  lng: number | null | undefined;
}

export const STOPS_PER_LINK = 10; // 1 destination + 9 waypoints

function validStop(s: RouteStop): s is { lat: number; lng: number } {
  return (
    typeof s.lat === "number" &&
    typeof s.lng === "number" &&
    Number.isFinite(s.lat) &&
    Number.isFinite(s.lng) &&
    !(s.lat === 0 && s.lng === 0) &&
    Math.abs(s.lat) <= 90 &&
    Math.abs(s.lng) <= 180
  );
}

const fmt = (s: { lat: number; lng: number }) => `${s.lat.toFixed(6)},${s.lng.toFixed(6)}`;

/**
 * One Google Maps link per leg of up to 10 stops, in the given order. Stops
 * without usable coordinates are skipped. Each leg after the first starts at
 * the previous leg's last stop, so the legs chain into one day.
 */
export function routeLinks(stops: RouteStop[]): string[] {
  const ok = stops.filter(validStop);
  const links: string[] = [];
  let origin: { lat: number; lng: number } | null = null;
  for (let i = 0; i < ok.length; i += STOPS_PER_LINK) {
    const leg = ok.slice(i, i + STOPS_PER_LINK);
    const destination = leg[leg.length - 1];
    const waypoints = leg.slice(0, -1);
    const params = new URLSearchParams({ api: "1", travelmode: "driving", destination: fmt(destination) });
    if (origin) params.set("origin", fmt(origin));
    if (waypoints.length) params.set("waypoints", waypoints.map(fmt).join("|"));
    links.push(`https://www.google.com/maps/dir/?${params.toString()}`);
    origin = destination;
  }
  return links;
}

/** How many of the stops have usable coordinates. */
export function routableCount(stops: RouteStop[]): number {
  return stops.filter(validStop).length;
}
